import React, { useState, useEffect } from "react";
import {
  Typography, Box, Paper, Chip, Tooltip, Button
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton } from "@mui/material";
import { StyledButton } from "../buttons/custom-button";
import { ReactComponent as Loader } from "../../../assests/logos/loader.svg";
import { atcService } from "../../../screens/alternative-transaction-certificates/atc-service/atc.service";
import useNotification from "../../hooks/useNotification";

interface FileUploaderProps {
  uniqueId: string;
  setFileName: (fileNames: string[]) => void;
  setFilePath: (filePaths: string[]) => void;
  onUploadSuccess?: (fileInfo: { fileName: string; file: File; blobPath: string }) => void;
  onUploadError?: (error: string) => void;
  onRemove?: () => void;
  atcNo: string;
  atcVersionNo: string;
  cbLicensingCode?: string;
  disabled?: boolean;
  uploadedFile?: File | null;
  uploadType?: string;
  dateOfIssue?: string;
}

const FileUploader = ({
  uniqueId,
  setFileName,
  setFilePath,
  onUploadSuccess,
  onUploadError,
  onRemove,
  atcNo,
  atcVersionNo,
  cbLicensingCode = "",
  disabled = false,
  uploadedFile,
  uploadType,
  dateOfIssue,
}: FileUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [storedBlobPath, setStoredBlobPath] = useState("");
  const { notify } = useNotification();



  // Sync selectedFile with uploadedFile prop on mount or when it changes
  useEffect(() => {
    if (uploadedFile) {
      setSelectedFile(uploadedFile);
      setIsFileUploaded(true); // Mark as uploaded if prop provides a file
    } else {
      setSelectedFile(null);
      setIsFileUploaded(false); // Reset upload status if no file
    }
  }, [uploadedFile]);

  // Clear uploaded file if atcNo or dateOfIssue change and filename no longer matches
  useEffect(() => {
    if (!selectedFile || !isFileUploaded) return;
    const validationError = validateFile(selectedFile);
    if (validationError) {
      notify(validationError, "error");
      setSelectedFile(null);
      setIsFileUploaded(false);
      setStoredBlobPath("");
      setError(validationError);
      onRemove?.();
    }
  }, [dateOfIssue, atcNo]); // re-run when fields that appear in the filename change

  // Validate ATU PDF naming convention: ATU_CBCode_ATU_Number_YYYYMMDD.pdf
  const validateFile = (file: File): string | null => {
    const name = file.name.toLowerCase();
    const expectedFormat = "ATU_[CBCode]_[ATU-Number]_YYYYMMDD.pdf";

    if (!name.endsWith(".pdf"))
      return `Only PDF files are allowed. Expected format: ${expectedFormat}`;

    if (!name.startsWith("atu_"))
      return `Invalid file. Only ATU PDF files are allowed. Expected format: ${expectedFormat}`;

    // Full parse: atu_{cbCode}_{atcNo}_{YYYYMMDD}.pdf
    const fileMatch = name.match(/^atu_([^_]+)_(.+)_(\d{8})\.pdf$/i);
    if (!fileMatch)
      return `Filename must follow format: ${expectedFormat}`;

    const [, fileCbCode, fileAtcNo, fileDate] = fileMatch;

    if (cbLicensingCode && fileCbCode.toLowerCase() !== cbLicensingCode.toLowerCase())
      return `CB code in filename (${fileCbCode}) does not match the CB Licensing Code (${cbLicensingCode}). Expected format: ${expectedFormat}`;

    if (atcNo && fileAtcNo.toLowerCase() !== atcNo.toLowerCase())
      return `ATU number in filename (${fileAtcNo}) does not match the Transaction Certificate Number (${atcNo}). Expected format: ${expectedFormat}`;

    // form-builder stores dates as YYYY/MM/DD; also handle YYYY-MM-DD
    if (dateOfIssue) {
      const expectedDate = dateOfIssue.replace(/[-/]/g, "").substring(0, 8);
      if (fileDate !== expectedDate)
        return `Date in filename (${fileDate}) does not match the ATU Date of Issue (${expectedDate}). Expected format: ${expectedFormat}`;
    }

    return null;
  };

  // Upload file to backend
  const uploadFileToBackend = async (file: File): Promise<string | false> => {
    try {
      const sanitizeFilename = (name: string): string =>
        name.replace(/[^\w.-]/g, "_");

      const safeFilename = sanitizeFilename(file.name);
      const response = await atcService.uploadFile(file, {
        filename: safeFilename,
        atcNo,
        atcVersionNo,
        cbLicensingCode,
        uploadType: uploadType || "",
      });

      if (response?.status === 200) {
        // Use server-returned blobPath so it matches what the backend stored
        return ((response.data as any)?.blobPath as string) || file.name;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      return false;
    }
  };

  const processFiles = (files: FileList | File[]) => {
    let file = Array.from(files)[0];
    if (!file) return;

    // Strip duplicate .pdf extension caused by Windows hiding extensions
    if (file.name.toLowerCase().endsWith(".pdf.pdf")) {
      file = new File([file], file.name.slice(0, -4), { type: file.type });
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      notify(validationError, "error");
      return;
    }
    setSelectedFile(file);
    setError("");
    setIsFileUploaded(false);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || isFileUploaded) return;
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) { notify("Please fill in the TC Number, TC Version Number, Date of Issue and Standard fields before uploading.", "warning"); return; }
    if (isFileUploaded) return;
    if (e.dataTransfer.files?.length > 0) processFiles(e.dataTransfer.files);
  };

  const handleBrowseClick = () => {
    if (disabled) { notify("Please fill in the TC Number, TC Version Number, Date of Issue and Standard fields before uploading.", "warning"); return; }
    if (isFileUploaded) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = (e: any) => {
      if (e.target.files) processFiles(e.target.files);
    };
    input.click();
  };

  const handleUploadClick = async () => {
    if (disabled || !selectedFile) {
      setError(disabled ? "Form is not ready for upload" : "No file selected for upload.");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const blobPath = await uploadFileToBackend(selectedFile);
      if (blobPath !== false) {
        setFileName([selectedFile.name]);
        setFilePath([selectedFile.name]);
        setStoredBlobPath(blobPath);
        onUploadSuccess?.({ fileName: selectedFile.name, file: selectedFile, blobPath });
        setIsFileUploaded(true);
      } else {
        setError("File upload failed");
        onUploadError?.("File upload failed");
      }
    } catch (error) {
      const errorMessage = `Upload failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`;
      setError(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    if (disabled) return;
    setSelectedFile(null);
    setError("");
    setIsFileUploaded(false);
    setStoredBlobPath("");
    onRemove?.();
  };

  const uploadStatus = isFileUploaded ? "Uploaded" : selectedFile ? "Ready" : "Missing";

  const displayName = selectedFile ? selectedFile.name : "";

  return (
    <Box>
      {isUploading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "250px", flexDirection: "column" }}>
          <Loader style={{ width: 50, height: 50 }} />
          <Typography variant="body2" sx={{ mt: 2 }}>Uploading file...</Typography>
        </Box>
      ) : (
        <>
          {/* Expected filename — shown before upload so user can copy it and rename their local file */}
          {!isFileUploaded && cbLicensingCode && atcNo && dateOfIssue && (() => {
            const expectedName = `atu_${cbLicensingCode.toLowerCase()}_${atcNo.toLowerCase()}_${dateOfIssue.replace(/[-/]/g, "").substring(0, 8)}.pdf`;
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2, p: 1.5, borderRadius: 2, backgroundColor: "rgba(44,71,72,0.06)", border: "1px solid rgba(44,71,72,0.2)" }}>
                <Typography variant="caption" sx={{ flex: 1, wordBreak: "break-all", color: "#2C4748", fontWeight: 500 }}>
                  <strong>Expected filename:</strong> {expectedName}
                </Typography>
                <Tooltip title="Copy expected filename">
                  <IconButton
                    size="small"
                    onClick={() => { navigator.clipboard.writeText(expectedName); notify("Filename copied to clipboard", "success"); }}
                    sx={{ color: "#2C4748", flexShrink: 0 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })()}

          {/* Drag & Drop zone */}
          <Box
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            sx={{
              border: "2px dashed",
              borderColor: disabled || isFileUploaded ? "rgba(44,71,72,0.3)" : dragActive ? "#2C4748" : "rgba(44,71,72,0.3)",
              borderRadius: 4,
              py: 4,
              px: 3,
              textAlign: "center",
              cursor: disabled || isFileUploaded ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              backgroundColor: dragActive ? "rgba(44,71,72,0.06)" : "#FFFFFF",
              transition: "all 0.2s",
              mb: 3,
              "&:hover": {
                borderColor: disabled || isFileUploaded ? "rgba(44,71,72,0.3)" : "#2C4748",
                backgroundColor: disabled || isFileUploaded ? "#FFFFFF" : "rgba(44,71,72,0.02)",
              },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: "#2C4748", mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2C4748" }}>
              {isFileUploaded ? "ATU PDF Uploaded" : "Drag & Drop ATU PDF here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isFileUploaded ? "File uploaded successfully." : "Or click to browse from your computer"}
            </Typography>
          </Box>

          {/* Original Certificate slot */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              borderColor: isFileUploaded ? "#2d6a4f" : error ? "#d32f2f" : selectedFile ? "#2d6a4f" : "rgba(44,71,72,0.15)",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2C4748" }}>
                Original Certificate
              </Typography>
              {isFileUploaded ? (
                <Chip label="Uploaded" size="small" sx={{ fontWeight: "bold", backgroundColor: "#2d6a4f", color: "#FFFFFF" }} />
              ) : selectedFile ? (
                <Chip label="Ready" size="small" sx={{ fontWeight: "bold", backgroundColor: "#2d6a4f", color: "#FFFFFF" }} />
              ) : (
                <Chip label="Missing" size="small" variant="outlined" sx={{ borderStyle: "dashed", color: "rgba(44,71,72,0.6)", borderColor: "rgba(44,71,72,0.4)" }} />
              )}
            </Box>

            {/* File status area */}
            <Box sx={{ p: 1.5, borderRadius: 2, border: "1.5px dashed", borderColor: isFileUploaded ? "#2d6a4f" : error ? "#d32f2f" : selectedFile ? "#2d6a4f" : "rgba(44,71,72,0.2)", backgroundColor: isFileUploaded ? "rgba(45,106,79,0.02)" : "transparent" }}>
              {selectedFile ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Tooltip title={displayName} placement="top">
                    <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%", fontWeight: 500 }}>
                      {displayName}
                    </Typography>
                  </Tooltip>
                  {!disabled && (
                    <IconButton size="small" onClick={handleRemoveFile} sx={{ color: "#d32f2f", "&:hover": { backgroundColor: "#ffebee" } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">No file selected for Slot A</Typography>
                  {!disabled && (
                    <Button size="small" variant="text" onClick={handleBrowseClick} sx={{ textTransform: "none", fontWeight: 600, fontSize: "12px", mt: 0.5 }}>
                      Browse file directly
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1.5, fontSize: "12px" }}>
                {error}
              </Typography>
            )}
          </Paper>

          {/* Upload button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleUploadClick}
              disabled={!selectedFile || isUploading || disabled || isFileUploaded}
            >
              Upload File
            </StyledButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FileUploader;